
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { useToast } from "@/components/ui/use-toast";

// Import tab components
import AllVouchersTab from "./voucher-management/AllVouchersTab";
import AssignVoucherTab from "./voucher-management/AssignVoucherTab";
import VoucherHistoryTab from "./voucher-management/VoucherHistoryTab";
import VoucherSettingsTab from "./voucher-management/VoucherSettingsTab";
import { initializeVoucherDatabase } from "@/lib/db/voucherDb";

const VoucherManagement = () => {
  const [activeTab, setActiveTab] = useState("all-vouchers");
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [initAttempts, setInitAttempts] = useState(0);
  const { toast } = useToast();

  React.useEffect(() => {
    const initialize = async () => {
      try {
        console.log(`Initializing voucher database (attempt ${initAttempts + 1}) with timestamp-based versioning...`);
        setIsLoading(true);
        await initializeVoucherDatabase();
        console.log("Voucher database initialized successfully");
        setIsLoading(false);
        setInitError(null);
      } catch (error) {
        console.error("Failed to initialize voucher database", error);
        setInitError(`Database initialization failed (attempt ${initAttempts + 1}). This may be due to a schema update. Please try refreshing the page.`);
        toast({
          variant: "destructive",
          title: "Database initialization failed",
          description: "There was an error setting up the voucher database. Click retry or refresh the page.",
        });
        setIsLoading(false);
      }
    };
    
    initialize();
  }, [toast, initAttempts]);
  
  const handleRetry = () => {
    setInitError(null);
    setIsLoading(true);
    setInitAttempts(prev => prev + 1); // This will trigger the useEffect again
  };

  if (isLoading) {
    return <LayoutLoader message="Initializing voucher database..." />;
  }

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <h2 className="text-xl font-semibold text-red-600 mb-4">{initError}</h2>
        <p className="text-muted-foreground mb-6">This could be due to browser storage issues or a temporary system error.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={handleRetry}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90"
          >
            Retry Initialization
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="border border-input bg-background px-4 py-2 rounded hover:bg-accent"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Voucher Management</h1>
        <p className="text-muted-foreground">
          Create, assign, and track government-funded vouchers for eligible customers and coop members.
        </p>
      </div>

      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full border-b justify-start">
          <TabsTrigger value="all-vouchers" className="px-8 py-2">All Vouchers</TabsTrigger>
          <TabsTrigger value="assign-voucher" className="px-8 py-2">Assign Voucher</TabsTrigger>
          <TabsTrigger value="voucher-history" className="px-8 py-2">Voucher History</TabsTrigger>
          <TabsTrigger value="voucher-settings" className="px-8 py-2">Voucher Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all-vouchers" className="space-y-4">
          <AllVouchersTab />
        </TabsContent>
        
        <TabsContent value="assign-voucher" className="space-y-4">
          <AssignVoucherTab />
        </TabsContent>
        
        <TabsContent value="voucher-history" className="space-y-4">
          <VoucherHistoryTab />
        </TabsContent>
        
        <TabsContent value="voucher-settings" className="space-y-4">
          <VoucherSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VoucherManagement;
